// cache-bust: v1.3
import PropTypes from 'prop-types';
// React hooks for form management and component lifecycle
import { useCallback, useMemo } from 'react';
// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
// hooks
import { useDynamicForm } from 'src/hooks/use-dynamic-form';
import { useSiteSettings } from 'src/hooks/use-site-settings';
// components
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider from 'src/components/hook-form';
import DynamicFormRenderer from 'src/components/form-fields/DynamicFormRenderer';
import SubmissionLocationSelector from 'src/sections/data-collection/components/SubmissionLocationSelector';

// ----------------------------------------------------------------------

/**
 * Converts a flat fields array (with section_break markers) into the
 * DynamicFormRenderer section-group format: [{ title, fields }]
 * If there are no section_break markers, returns the flat array unchanged.
 */
function groupFieldsIntoSections(flatFields) {
  const hasBreaks = flatFields.some(f => f.type === 'section_break');
  if (!hasBreaks) return flatFields;

  const sections = [];
  let current = { title: '', fields: [], sectionField: null };

  flatFields.forEach(f => {
    if (f.type === 'section_break') {
      if (current.fields.length > 0 || current.title || current.sectionField) {
        sections.push(current);
      }
      current = { title: f.label || '', fields: [], sectionField: f };
    } else {
      current.fields.push(f);
    }
  });
  if (current.fields.length > 0 || current.title || current.sectionField) {
    sections.push(current);
  }

  return sections;
}

// ----------------------------------------------------------------------

export default function QuickFormDialog({
  open,
  onClose,
  onSubmit,
  title = 'Create New Item',
  titleIcon,
  submitButtonText,
  cancelButtonText = 'Cancel',
  fields = [],
  validationSchema,
  defaultValues,
  maxWidth = 'md',
  showAvatar = false,
  avatarHelperText = '',
  successMessage = 'Success!',
  errorMessage = 'Something went wrong!',
  currentData = null,
  isLoading = false,
  formType,
  primaryFormId,
  isVerificationRequired = true,
  autoFillLookupFieldId,
  projectId,
  isPublic,
  isCustomForm,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { settings: siteSettings, loading: siteSettingsLoading } = useSiteSettings();

  // Flat fields without section_break — used for schema/validation building
  const flatFields = useMemo(
    () => fields.filter(f => f.type !== 'section_break'),
    [fields]
  );

  // Grouped section format for DynamicFormRenderer
  const rendererFields = useMemo(
    () => groupFieldsIntoSections(fields),
    [fields]
  );

  const methods = useDynamicForm({
    fields: flatFields,
    validationSchema,
    defaultValues,
    currentData: useMemo(() => {
      if (currentData && formType === 'autoFill') {
        return { ...currentData, _isAutoFillVerified: true };
      }
      return currentData;
    }, [currentData, formType]),
    showAvatar,
    siteSettings,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onInvalid = (errors) => {
    console.error('Validation errors:', errors);
    // enqueueSnackbar('Please check the form for errors', { variant: 'error' });
  };

  const handleFormSubmit = handleSubmit(async (data) => {
    // 🛡️ Auto-Fill Verification Enforcement
    const isStrictVerification = Boolean(isVerificationRequired);
    if (formType === 'autoFill' && isStrictVerification && !data._isAutoFillVerified) {
      enqueueSnackbar('Please verify the identity field before saving.', { variant: 'error' });
      return;
    }

    try {
      await onSubmit(data);
      reset();
      onClose();
    } catch (error) {
      console.error(error);
      const msg = error.message || errorMessage;
      enqueueSnackbar(msg, { variant: 'error' });
    }
  }, onInvalid);

  const onSubmitWrapper = (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    return handleFormSubmit(e);
  };

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  return (
    <Dialog
      fullWidth
      maxWidth={maxWidth}
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { maxHeight: '90vh' },
      }}
    >
      <FormProvider methods={methods} onSubmit={onSubmitWrapper}>
        <DialogTitle sx={{ pb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Iconify
              icon={
                titleIcon ||
                (currentData && (currentData.id || currentData._id)
                  ? 'solar:pen-bold'
                  : 'mingcute:add-line')
              }
              width={24}
            />
            <Box>{title}</Box>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 3 }}>
          {/* Show loading spinner while fetching record or site settings */}
          {isLoading || siteSettingsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <Stack spacing={2} alignItems="center">
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Loading record data...
                </Typography>
              </Stack>
            </Box>
          ) : (
            <>
              {isCustomForm && (
                <SubmissionLocationSelector projectId={projectId} isPublic={isPublic} />
              )}

              <DynamicFormRenderer
                fields={rendererFields}
                showAvatar={showAvatar}
                avatarHelperText={avatarHelperText}
                EntityCreationDialog={QuickFormDialog}
                formType={formType}
                primaryFormId={primaryFormId}
                isVerificationRequired={Boolean(isVerificationRequired)}
                autoFillLookupFieldId={autoFillLookupFieldId}
              />
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            {cancelButtonText}
          </Button>

          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            {submitButtonText ||
              (currentData && (currentData.id || currentData._id) ? 'Update' : 'Create')}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

QuickFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  title: PropTypes.string,
  titleIcon: PropTypes.string,
  submitButtonText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      validation: PropTypes.object,
      defaultValue: PropTypes.any,
      options: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.func
      ]),
      gridProps: PropTypes.object,
    })
  ),
  validationSchema: PropTypes.object,
  defaultValues: PropTypes.object,
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  showAvatar: PropTypes.bool,
  avatarHelperText: PropTypes.string,
  successMessage: PropTypes.string,
  errorMessage: PropTypes.string,
  currentData: PropTypes.object,
  isLoading: PropTypes.bool,
  formType: PropTypes.string,
  primaryFormId: PropTypes.string,
  isVerificationRequired: PropTypes.bool,
  autoFillLookupFieldId: PropTypes.string,
  projectId: PropTypes.string,
  isPublic: PropTypes.bool,
  isCustomForm: PropTypes.bool,
};
